import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  FolderKanban, CheckSquare, AlertTriangle, Clock,
  ChevronRight, Circle, CheckCircle2, AlertCircle,
  TrendingUp, TrendingDown, Wallet,
} from 'lucide-react'

const oggi = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const tra7giorni = () => {
  const d = oggi()
  d.setDate(d.getDate() + 7)
  return d
}

const formatDate = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })

const fmt = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)

const saluto = () => {
  const h = new Date().getHours()
  if (h < 12) return 'Buongiorno'
  if (h < 18) return 'Buon pomeriggio'
  return 'Buonasera'
}

// ─── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    onClick={onClick}
    className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors w-full text-left"
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  </button>
)

// ─── MiniTask ──────────────────────────────────────────────────────────────────
const MiniTask = ({ task, onToggle }) => {
  const scaduta = task.scadenza && new Date(task.scadenza + 'T00:00:00') < oggi()
  const isDone = task.status === 'done'

  return (
    <div className={`flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-gray-800/60 transition-colors group ${isDone ? 'opacity-50' : ''}`}>
      <button onClick={() => onToggle(task)} className="flex-shrink-0">
        {isDone
          ? <CheckCircle2 size={16} className="text-green-500" />
          : <Circle size={16} className="text-gray-600 group-hover:text-gray-400 transition-colors" />
        }
      </button>
      <span className={`flex-1 text-sm min-w-0 truncate ${isDone ? 'line-through text-gray-500' : 'text-white'}`}>
        {task.titolo}
      </span>
      {task.scadenza && (
        <span className={`text-xs flex-shrink-0 flex items-center gap-1 ${scaduta ? 'text-red-400' : 'text-gray-500'}`}>
          {scaduta && <AlertCircle size={11} />}
          {formatDate(task.scadenza)}
        </span>
      )}
    </div>
  )
}

// ─── BarChart ──────────────────────────────────────────────────────────────────
const BarChart = ({ data }) => {
  const maxVal = Math.max(...data.map(d => Math.max(d.entrate, d.uscite)), 1)

  return (
    <div className="flex items-end gap-3 h-32 mt-2">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="flex items-end gap-0.5 h-24 w-full justify-center">
            {/* Entrate */}
            <div
              className="flex-1 bg-green-500/60 rounded-t hover:bg-green-500/80 transition-colors"
              style={{ height: `${(d.entrate / maxVal) * 100}%`, minHeight: d.entrate > 0 ? '4px' : '0' }}
              title={`Entrate: ${fmt(d.entrate)}`}
            />
            {/* Uscite */}
            <div
              className="flex-1 bg-red-500/50 rounded-t hover:bg-red-500/70 transition-colors"
              style={{ height: `${(d.uscite / maxVal) * 100}%`, minHeight: d.uscite > 0 ? '4px' : '0' }}
              title={`Uscite: ${fmt(d.uscite)}`}
            />
          </div>
          <span className="text-xs text-gray-500 truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { profile, getDisplayName } = useAuth()
  const navigate = useNavigate()

  const [progettiAttivi, setProgettiAttivi] = useState([])
  const [tasks, setTasks] = useState([])
  const [financeData, setFinanceData] = useState({ mesi: [], topProgetti: [], totEntrate: 0, totUscite: 0 })
  const [loading, setLoading] = useState(true)

  const fetchDati = useCallback(async () => {
    const myId = profile?.id
    const [{ data: pr }, { data: tk }] = await Promise.all([
      supabase
        .from('progetti')
        .select('id, titolo, colore, status, scadenza, project_members!inner(user_id)')
        .eq('status', 'attivo')
        .eq('project_members.user_id', myId)
        .order('created_at', { ascending: false }),
      supabase
        .from('tasks')
        .select('id, titolo, status, priorita, scadenza, project_id, assegnato_a, parent_id')
        .neq('status', 'done'),
    ])
    setProgettiAttivi(pr || [])
    setTasks(tk || [])
    setLoading(false)
  }, [profile?.id])

  const fetchFinance = useCallback(async () => {
    // Ultimi 6 mesi
    const seimesiFA = new Date()
    seimesiFA.setMonth(seimesiFA.getMonth() - 5)
    seimesiFA.setDate(1)
    const from = seimesiFA.toISOString().split('T')[0]

    const { data: voci } = await supabase
      .from('finance_voci')
      .select('tipo, importo, data, project_id')
      .gte('data', from)

    if (!voci?.length) return

    // Raggruppa per mese
    const mesiMap = {}
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`
      const label = d.toLocaleDateString('it-IT', { month: 'short' })
      mesiMap[key] = { label, entrate: 0, uscite: 0 }
    }

    voci.forEach(v => {
      const key = v.data.slice(0, 7)
      if (mesiMap[key]) {
        if (v.tipo === 'entrata') mesiMap[key].entrate += Number(v.importo)
        else mesiMap[key].uscite += Number(v.importo)
      }
    })

    const mesi = Object.values(mesiMap)
    const totEntrate = voci.filter(v => v.tipo === 'entrata').reduce((s, v) => s + Number(v.importo), 0)
    const totUscite  = voci.filter(v => v.tipo === 'uscita').reduce((s, v) => s + Number(v.importo), 0)

    // Top progetti per entrate
    const perProgetto = {}
    voci.filter(v => v.tipo === 'entrata' && v.project_id).forEach(v => {
      if (!perProgetto[v.project_id]) perProgetto[v.project_id] = 0
      perProgetto[v.project_id] += Number(v.importo)
    })

    // Fetch nomi progetti
    const topIds = Object.entries(perProgetto)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([id]) => id)

    let topProgetti = []
    if (topIds.length > 0) {
      const { data: prs } = await supabase
        .from('progetti')
        .select('id, titolo, colore')
        .in('id', topIds)
      topProgetti = topIds.map(id => ({
        ...prs?.find(p => p.id === id),
        entrate: perProgetto[id],
      })).filter(Boolean)
    }

    setFinanceData({ mesi, topProgetti, totEntrate, totUscite })
  }, [])

  useEffect(() => {
    fetchDati()
    fetchFinance()
    const channel = supabase
      .channel('dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, fetchDati)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'progetti' }, fetchDati)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchDati, fetchFinance])

  const handleToggle = async (task) => {
    await supabase
      .from('tasks')
      .update({ status: 'done', updated_at: new Date().toISOString() })
      .eq('id', task.id)
    setTasks(prev => prev.filter(t => t.id !== task.id))
  }

  const rootTasks = tasks.filter(t => !t.parent_id)
  const taskScadenteOggi = rootTasks.filter(t =>
    t.scadenza && new Date(t.scadenza + 'T00:00:00').getTime() === oggi().getTime()
  )
  const taskAltaPriorita = rootTasks.filter(t => t.priorita === 'alta')
  const mieTaskUrgenti = rootTasks
    .filter(t => t.priorita === 'alta' && t.assegnato_a === profile?.id)
    .slice(0, 5)
  const taskSettimana = rootTasks
    .filter(t => {
      if (!t.scadenza) return false
      const d = new Date(t.scadenza + 'T00:00:00')
      return d >= oggi() && d <= tra7giorni()
    })
    .sort((a, b) => new Date(a.scadenza) - new Date(b.scadenza))
    .slice(0, 6)

  const taskPerProgetto = (progettoId) =>
    rootTasks.filter(t => t.project_id === progettoId)

  const nome = getDisplayName()
  const { mesi, topProgetti, totEntrate, totUscite } = financeData
  const hasFin = totEntrate > 0 || totUscite > 0
  const maxTop = topProgetti.length > 0 ? topProgetti[0].entrate : 1

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="space-y-8">

      {/* Saluto */}
      <div>
        <h1 className="text-2xl font-bold text-white">{saluto()}, {nome} 👋</h1>
        <p className="text-gray-500 text-sm mt-1">Ecco la situazione di oggi.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard icon={FolderKanban} label="Progetti attivi" value={progettiAttivi.length}
          color="bg-violet-500/20 text-violet-400" onClick={() => navigate('/progetti')} />
        <StatCard icon={CheckSquare} label="Task aperte" value={rootTasks.length}
          color="bg-blue-500/20 text-blue-400" onClick={() => navigate('/tasks')} />
        <StatCard icon={Clock} label="Scadono oggi" value={taskScadenteOggi.length}
          color={taskScadenteOggi.length > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-800 text-gray-500'}
          onClick={() => navigate('/tasks')} />
        <StatCard icon={AlertTriangle} label="Alta priorità" value={taskAltaPriorita.length}
          color={taskAltaPriorita.length > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-500'}
          onClick={() => navigate('/tasks')} />
      </div>

      {/* Corpo principale */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* Le mie task urgenti */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">Le mie task urgenti</h2>
            <button onClick={() => navigate('/tasks')} className="text-gray-500 hover:text-violet-400 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="px-3 py-2">
            {mieTaskUrgenti.length === 0 ? (
              <p className="text-gray-600 text-sm text-center py-6">Nessuna task urgente assegnata a te</p>
            ) : mieTaskUrgenti.map(task => (
              <MiniTask key={task.id} task={task} onToggle={handleToggle} />
            ))}
          </div>
        </div>

        {/* Task in scadenza */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">In scadenza questa settimana</h2>
            <button onClick={() => navigate('/tasks')} className="text-gray-500 hover:text-violet-400 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="px-3 py-2">
            {taskSettimana.length === 0 ? (
              <p className="text-gray-600 text-sm text-center py-6">Nessuna scadenza nei prossimi 7 giorni</p>
            ) : taskSettimana.map(task => (
              <MiniTask key={task.id} task={task} onToggle={handleToggle} />
            ))}
          </div>
        </div>

      </div>

      {/* ── Dashboard Finanziaria ── */}
      {hasFin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Panoramica finanziaria · ultimi 6 mesi</h2>
            <button onClick={() => navigate('/finance')} className="text-xs text-violet-400 hover:text-violet-300 transition-colors flex items-center gap-1">
              Vai a Finance <ChevronRight size={13} />
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

            {/* Grafico mensile */}
            <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-2.5 h-2.5 rounded bg-green-500/60 inline-block" /> Entrate
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-2.5 h-2.5 rounded bg-red-500/50 inline-block" /> Uscite
                </div>
              </div>
              <BarChart data={mesi} />
            </div>

            {/* Totali + top progetti */}
            <div className="space-y-3">
              <div className="bg-green-500/10 border border-green-500/20 rounded-2xl px-4 py-3 flex items-center gap-3">
                <TrendingUp size={18} className="text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Entrate (6 mesi)</p>
                  <p className="text-base font-bold text-green-400">{fmt(totEntrate)}</p>
                </div>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3 flex items-center gap-3">
                <TrendingDown size={18} className="text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Uscite (6 mesi)</p>
                  <p className="text-base font-bold text-red-400">{fmt(totUscite)}</p>
                </div>
              </div>
              <div className={`border rounded-2xl px-4 py-3 flex items-center gap-3 ${totEntrate - totUscite >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                <Wallet size={18} className={totEntrate - totUscite >= 0 ? 'text-blue-400 flex-shrink-0' : 'text-orange-400 flex-shrink-0'} />
                <div>
                  <p className="text-xs text-gray-500">Saldo</p>
                  <p className={`text-base font-bold ${totEntrate - totUscite >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>{fmt(totEntrate - totUscite)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top progetti per entrate */}
          {topProgetti.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Top progetti per entrate</p>
              <div className="space-y-3">
                {topProgetti.map(p => (
                  <div key={p.id} className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />
                    <span className="text-sm text-white flex-1 min-w-0 truncate">{p.titolo}</span>
                    <span className="text-sm font-semibold text-green-400 flex-shrink-0">{fmt(p.entrate)}</span>
                    <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden flex-shrink-0">
                      <div
                        className="h-full bg-green-500/60 rounded-full"
                        style={{ width: `${(p.entrate / maxTop) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Progetti attivi */}
      {progettiAttivi.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">Progetti attivi</h2>
            <button onClick={() => navigate('/progetti')} className="text-gray-500 hover:text-violet-400 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="divide-y divide-gray-800">
            {progettiAttivi.map(p => {
              const ptasks = taskPerProgetto(p.id)
              return (
                <div key={p.id} onClick={() => navigate('/progetti')}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-gray-800/40 transition-colors cursor-pointer"
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />
                  <span className="flex-1 text-sm text-white font-medium truncate">{p.titolo}</span>
                  {p.scadenza && <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(p.scadenza)}</span>}
                  <span className="text-xs text-gray-500 flex-shrink-0 bg-gray-800 px-2 py-0.5 rounded-full">
                    {ptasks.length} task aperte
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

    </div>
  )
}

export default Dashboard
